// Fachlogik von "Aus dem Gespräch" ohne Oberfläche. Herausgelöst aus
// VerhandlungsPassus.jsx, VerhandlungsKarten.jsx und gespraechAnzeige.jsx
// (23.09.2026), damit Desktop und Handy dieselben Nutzlasten, Zähler und Texte
// benutzen.
//
// Die Regeln dahinter stehen in AGENTS.md ("Verhandlungspassus"):
// Freigegebenes ("passt") und Zugestimmtes ändert die KI nie, gelöscht wird
// nur auf Verlangen, "Entwürfe aufräumen" geht ohne KI.
//
// Im Code heißen Ergänzungen "passus" und Ersetzungen "aenderungen" - die
// Namen stecken in API-Operationen und gespeicherten Sitzungen.

import axios from 'axios';

// Was die Ladeanzeige während einer Gesprächsrunde durchlaufen lässt.
export const GESPRAECH_SCHRITTE = [
  'Liest deine Nachricht …',
  'Gleicht mit dem Vertrag ab …',
  'Prüft deine Liste …',
  'Formuliert die Ergänzungen …',
];

export const GESPRAECH_WEGWEISER = [
  ['Besprochenes eingeben', 'Stichpunkte, eine Mail oder eine Datei'],
  ['Ergänzungen prüfen', 'freigeben, überarbeiten oder verwerfen'],
  ['In den Vertrag', 'nur Freigegebenes wird eingefügt'],
];

export const ENTWERFEN_NACHRICHT = 'Arbeite aus dem bisherigen Gespräch die Punkte heraus, die vertraglich '
  + 'geregelt werden müssen, und formuliere sie als Passus.';

export const UEBERARBEITEN_PLATZHALTER = 'Was soll anders werden? Etwa: Frist auf 14 Tage, Kündigung nur schriftlich.';

export const EINFUEGE_LABEL = {
  'before-signatures': 'Vor den Unterschriften',
  'after-preamble': 'Nach der Präambel',
  'end-of-contract': 'Am Ende des Vertrags',
};

// ---------- Nutzlasten ----------

export function gespraechAdresse(apiBase, dealId) {
  return `${apiBase}/contracts/state?resource=verhandlung&vorgang=${encodeURIComponent(dealId)}`;
}

export function gespraechPostAdresse(apiBase) {
  return `${apiBase}/contracts/state?resource=verhandlung`;
}

/** Der Körper jedes POST. Ein Gespräch gehört immer zu einem Deal. */
export function gespraechKoerper(dealId, contractKey, nutzlast) {
  return { vorgang: dealId, contractKey, ...nutzlast };
}

/**
 * erstGliedern (seit 29.09.2026, nur am Desktop): Ein langes Schreiben als
 * Nachricht gliedert der Server zuerst, statt es in einem Zug zu formulieren
 * - ausser bei "1 zu 1". Das Handy schickt es nicht, es kennt die
 * Gliederung noch nicht.
 */
export function chatNutzlast({ nachricht, vertragsart, paragraphen, vertragsHtml, erstGliedern = false }) {
  return { op: 'chat', nachricht, vertragsart, paragraphen, vertragsHtml, ...(erstGliedern ? { erstGliedern: true } : {}) };
}

/**
 * vertragsHtml (seit 08.10.2026): Würde der Passus ein eigener Paragraph,
 * prüft der Server am Wortlaut, ob er nicht in einen bestehenden gehört.
 */
export function ueberarbeitenNutzlast({ passusId, hinweis, vertragsart, paragraphen, vertragsHtml }) {
  return { op: 'ueberarbeiten', passusId, hinweis, vertragsart, paragraphen, ...(vertragsHtml ? { vertragsHtml } : {}) };
}

/** Stand einer Ergänzung; hinweis nur, wenn einer mitgegeben wurde ("Nur vormerken"). */
export function standNutzlast(passusId, stand, hinweis) {
  return { op: 'stand', passusId, stand, ...(typeof hinweis === 'string' ? { hinweis } : {}) };
}

/** Stand einer Ersetzung. Vor dem Zustimmen prüft der Server am Vertragstext, ob sie anwendbar ist. */
export function aenderungStandNutzlast(aenderungId, stand, vertragsHtml) {
  return { op: 'aenderung-stand', aenderungId, stand, ...(stand === 'abgelehnt' ? {} : { vertragsHtml }) };
}

/**
 * "Neu beginnen" (seit 29.09.2026): leert Verlauf, Notizen und Gliederung,
 * mit alles auch Ergänzungen und Ersetzungen. Der Server sichert vorher;
 * "gespraech-zurueckholen" macht es einmal rückgängig.
 */
export function zuruecksetzenNutzlast(alles) {
  return { op: 'gespraech-zuruecksetzen', alles: alles === true };
}

export function zurueckgesetztMeldung(alles) {
  return alles
    ? 'Alles zurückgesetzt — du beginnst neu. Was schon im Vertrag steht, bleibt dort.'
    : 'Gespräch geleert — Ergänzungen und Ersetzungen sind noch da.';
}

export function zuErsetzungNutzlast({ passusId, vertragsart, vertragsHtml }) {
  return { op: 'passus-zu-ersetzung', passusId, vertragsart, vertragsHtml };
}

/** Die Anweisung für "Angleichen": nur die Parteibezeichnungen, sonst kein Wort. */
export function angleichenHinweis(parteien) {
  return `Gleiche die Parteibezeichnungen an den Vertrag an: ENPOWER heißt dort „${parteien.wir}“, `
    + `der Kunde „${parteien.kunde}“ — samt Artikel, Pronomen und Beugung. Sonst kein Wort ändern.`;
}

// ---------- Zähler ----------

export function gespraechZaehler(passusListe) {
  const liste = passusListe || [];
  return {
    gesamt: liste.filter((p) => p.stand !== 'verworfen').length,
    passt: liste.filter((p) => p.stand === 'passt').length,
    offen: liste.filter((p) => p.stand === 'entwurf' || p.stand === 'ueberarbeiten').length,
  };
}

/** Was "Entwürfe aufräumen" entfernen würde: alles außer Freigegebenem und Zugestimmtem. */
export function aufraeumbarZahl(passusListe, aenderungen) {
  return (passusListe || []).filter((p) => p.stand !== 'passt').length
    + (aenderungen || []).filter((a) => a.stand !== 'zugestimmt').length;
}

export function ersetzungenWort(anzahl) {
  return `${anzahl} ${anzahl === 1 ? 'Ersetzung' : 'Ersetzungen'}`;
}

// ---------- Was eine Runde bewirkt hat ----------

// Die Bezüge im Bericht des Servers (gespraechsListe.js): "P2" ist eine
// Ergänzung, "E1" eine Ersetzung.
function istErsetzungsBezug(bezug) {
  return /^e\d/i.test(String(bezug || '').trim());
}

/** "2 neue Ergänzungen · 1 Ersetzung überarbeitet" - was eine Runde an den Listen getan hat. */
export function berichtTeile(bericht) {
  if (!bericht) return [];
  const teile = [];
  // bericht.neu zählt nur Passus; neue Ersetzungen zählt rundenErgebnis selbst.
  if (bericht.neu) teile.push(`${bericht.neu} ${bericht.neu === 1 ? 'neue Ergänzung' : 'neue Ergänzungen'}`);
  // Getrennt nach Art (seit 24.10.2026): "1 überarbeitet" sagte nicht, ob die
  // Ergänzung oder die Ersetzung gemeint war.
  const ueberarbeitet = bericht.ueberarbeitet || [];
  const ersetzungen = ueberarbeitet.filter(istErsetzungsBezug).length;
  const ergaenzungen = ueberarbeitet.length - ersetzungen;
  if (ergaenzungen) teile.push(`${ergaenzungen} ${ergaenzungen === 1 ? 'Ergänzung' : 'Ergänzungen'} überarbeitet`);
  if (ersetzungen) teile.push(`${ersetzungenWort(ersetzungen)} überarbeitet`);
  if (bericht.entfernt?.length) teile.push(`${bericht.entfernt.length} entfernt`);
  if (bericht.verworfen?.length) teile.push(`${bericht.verworfen.length} verworfen`);
  // Wiederholt die KI nur, was schon dasteht, soll man das auch erfahren -
  // statt einer Antwort "neu angelegt" ohne sichtbare Änderung.
  if (bericht.doppelt?.length) teile.push(`${bericht.doppelt.length} schon vorhanden`);
  // Ersetzungen, die mit einer verworfenen Ergänzung gefallen sind (seit 29.09.2026).
  if (bericht.mitAbgelehnt) teile.push(`${ersetzungenWort(bericht.mitAbgelehnt)} mit abgelehnt`);
  // Lücken, die die KI mit Werten aus dem Gespräch gefüllt hat (seit 24.10.2026).
  const luecken = Array.isArray(bericht.luecken) ? bericht.luecken : [];
  if (luecken.length) {
    const werte = luecken.map((l) => `${l.luecke} → „${l.wert}“`).join(', ');
    const zurueck = luecken.some((l) => l.zurueck) ? ' – bitte erneut zustimmen' : '';
    teile.push(`${luecken.length === 1 ? 'Lücke gefüllt' : `${luecken.length} Lücken gefüllt`}: ${werte}${zurueck}`);
  }
  return teile;
}

/**
 * Was eine Standänderung einer Ergänzung mit ihren Ersetzungen gemacht hat
 * (Server: mitAbgelehnt, warenZugestimmt, zurueckgeholt, mitEntfernt).
 * Leer, wenn nichts mitging.
 */
export function folgeMeldung(daten) {
  if (!daten) return '';
  if (daten.mitAbgelehnt) {
    const zugestimmt = daten.warenZugestimmt
      ? ` ${daten.warenZugestimmt === 1 ? 'Eine davon war' : `${daten.warenZugestimmt} davon waren`} schon zugestimmt — `
        + 'falls schon in den Vertrag übernommen, dort von Hand zurücknehmen.'
      : '';
    return `${ersetzungenWort(daten.mitAbgelehnt)} dieser Ergänzung mit abgelehnt.${zugestimmt}`;
  }
  if (daten.zurueckgeholt) {
    return `${ersetzungenWort(daten.zurueckgeholt)} dieser Ergänzung wieder als Entwurf da — bitte neu prüfen.`;
  }
  if (daten.mitEntfernt) return `${ersetzungenWort(daten.mitEntfernt)} dieser Ergänzung mit entfernt.`;
  return '';
}

/**
 * Welche Entscheidungen aus den Reitern die KI in dieser Runde kannte (Server:
 * beruecksichtigt, seit 29.09.2026) - "Die KI kannte deine Entscheidungen —
 * Ergänzungen: 2 freigegeben, 1 verworfen · Ersetzungen: 1 abgelehnt".
 * Leer, wenn es keine gab. Vorher fragte die KI auf "ich habe die Punkte
 * durchgearbeitet" zurück, wie entschieden worden sei.
 */
export function beruecksichtigtText(zahl) {
  const z = zahl || {};
  const liste = (arten) => arten.filter((a) => z[a]).map((a) => `${z[a]} ${a}`).join(', ');
  const teile = [];
  const ergaenzungen = liste(['freigegeben', 'verworfen', 'vorgemerkt']);
  const ersetzungen = liste(['zugestimmt', 'abgelehnt']);
  if (ergaenzungen) teile.push(`Ergänzungen: ${ergaenzungen}`);
  if (ersetzungen) teile.push(`Ersetzungen: ${ersetzungen}`);
  return teile.length ? `Die KI kannte deine Entscheidungen — ${teile.join(' · ')}` : '';
}

/**
 * Aus der Antwort einer Runde: { teile, ziel, nichtVerwendet, beruecksichtigt }.
 * ziel ist der Reiter, den "Ansehen" öffnet ('passus' oder 'aenderungen').
 * aenderungenVorher: die Ersetzungen vor der Runde - neue zählen extra.
 */
export function rundenErgebnis(daten, aenderungenVorher) {
  const vorher = new Set((aenderungenVorher || []).map((a) => a.id));
  const neueErsetzungen = (daten?.sitzung?.aenderungen || []).filter((a) => !vorher.has(a.id)).length;
  const teile = berichtTeile(daten?.bericht);
  if (neueErsetzungen) teile.push(`${neueErsetzungen} ${neueErsetzungen === 1 ? 'neue Ersetzung' : 'neue Ersetzungen'}`);
  if (daten?.bericht?.geschuetzt?.length) teile.push('Freigegebenes unverändert');
  // "Ansehen" führt dorthin, wo sich etwas getan hat. Bis 24.10.2026 zählte nur
  // eine NEUE Ersetzung - überarbeitete die KI eine bestehende, führte
  // „1 überarbeitet · Ansehen“ in den leeren Reiter Ergänzungen.
  const bezuege = [
    ...(daten?.bericht?.ueberarbeitet || []),
    ...(daten?.bericht?.verworfen || []),
    ...(daten?.bericht?.entfernt || []),
    ...(daten?.bericht?.luecken || []).map((l) => l.ref),
  ];
  const ergaenzungenBewegt = Boolean(daten?.bericht?.neu) || bezuege.some((b) => !istErsetzungsBezug(b));
  const ersetzungenBewegt = Boolean(neueErsetzungen) || bezuege.some(istErsetzungsBezug);
  return {
    teile,
    ziel: ersetzungenBewegt && !ergaenzungenBewegt ? 'aenderungen' : 'passus',
    nichtVerwendet: Array.isArray(daten?.nichtVerwendet) ? daten.nichtVerwendet : [],
    beruecksichtigt: beruecksichtigtText(daten?.beruecksichtigt),
  };
}

/** Bei "1 zu 1": nummerierte Absätze der Vorlage, die in keinem Passus stehen. */
export function fehlendAus(daten) {
  return Array.isArray(daten?.fehlend) ? daten.fehlend : [];
}

export function fehlendNummern(fehlend) {
  return (fehlend || []).map((f) => `(${f.nummer})`).join(', ');
}

export function fehlendAnfaenge(fehlend) {
  return (fehlend || []).slice(0, 3).map((f) => `(${f.nummer}) ${f.anfang} …`).join('  ·  ');
}

export function nachreichenNachricht(fehlend) {
  const eins = fehlend.length === 1;
  return `Bitte übernimm auch ${eins ? 'Absatz' : 'die Absätze'} ${fehlendNummern(fehlend)} noch wörtlich — ${eins ? 'er fehlt' : 'sie fehlen'} in der Liste.`;
}

export function aufraeumenFrage(anzahl) {
  return `${anzahl} ${anzahl === 1 ? 'Entwurf' : 'Entwürfe'} entfernen? Freigegebenes bleibt.`;
}

export function aufraeumenMeldung(entfernt) {
  return entfernt ? `${entfernt} Entwürfe entfernt — Freigegebenes bleibt.` : 'Es gab nichts aufzuräumen.';
}

// ---------- Karten ----------

/**
 * Wohin und wie die Ergänzung kommt. `einfuegenAls` rechnet der Server aus
 * (einfuegeForm in api/_lib/verhandlungsStore.js, seit 07.10.2026): neuer
 * nummerierter Absatz eines Paragraphen oder eigener Paragraph. Seit
 * 08.10.2026 auch: unter dem Absatz, den der Passus berührt (`unterAbsatz`).
 */
export function einfuegeOrt(passus) {
  if (passus?.einfuegenAls === 'absatz' && passus?.paragraphVorschlag) {
    return passus.unterAbsatz
      ? `In „${passus.paragraphVorschlag}“ unter Abs. ${passus.unterAbsatz}`
      : `Neuer Absatz in „${passus.paragraphVorschlag}“`;
  }
  if (passus?.einfuegenAls === 'paragraph') {
    return 'Eigener Paragraph vor den Unterschriften';
  }
  return passus?.paragraphVorschlag
    ? `Ans Ende von „${passus.paragraphVorschlag}“`
    : (EINFUEGE_LABEL[passus?.insert_position] || passus?.insert_position || '');
}

/**
 * Dasselbe für die Zeile „Stelle“ oben in der Karte (seit 24.10.2026), zerlegt
 * in { wo, zusatz }: „wo“ ist der Paragraph und steht hervorgehoben, „zusatz“
 * sagt, wie der Passus dort hinkommt.
 */
export function ergaenzungsOrt(passus) {
  const paragraph = String(passus?.paragraphVorschlag || '').trim();
  if (passus?.einfuegenAls === 'absatz' && paragraph) {
    return passus.unterAbsatz
      ? { wo: paragraph, zusatz: `unter Abs. ${passus.unterAbsatz}` }
      : { wo: paragraph, zusatz: 'neuer Absatz am Ende' };
  }
  if (passus?.einfuegenAls === 'paragraph') {
    return { wo: 'Eigener Paragraph', zusatz: 'vor den Unterschriften' };
  }
  if (paragraph) return { wo: paragraph, zusatz: 'am Ende' };
  return { wo: EINFUEGE_LABEL[passus?.insert_position] || 'Vor den Unterschriften', zusatz: '' };
}

/** "„Anlagenbetreiberin“ → „Nutzer“ · …" aus abweichendeParteien (parteien.js). */
export function abweichendText(abweichend) {
  return (abweichend || []).map((x) => `„${x.steht}“ → „${x.soll}“`).join(' · ');
}

export function passusText(passus) {
  return (passus?.absaetze || []).map((a) => a.text).join('\n');
}

// ---------- Erst gliedern, dann entwerfen ----------
//
// Seit 29.09.2026 (api/_lib/verhandlungsGliederung.js). Ein langes Schreiben -
// typisch das Anwaltsschreiben der Eigentuemerseite - wird erst in Punkte
// zerlegt, ENPOWER hakt an, und nur das Angehakte wird entworfen, fuenf
// Punkte je Runde. Nichts ist vorgewaehlt: Die Punkte sind Forderungen der
// Gegenseite, keine Vereinbarung.

// Dieselben Zahlen stehen in api/_lib/verhandlungsGliederung.js.
export const GLIEDERUNG_MINDESTLAENGE = 1500;
export const GLIEDERUNG_PORTION = 5;

export const GLIEDERUNG_SCHRITTE = [
  'Liest das Schreiben …',
  'Fasst Doppeltes zusammen …',
  'Ordnet die Stellen im Vertrag zu …',
];

export const GLIEDERUNG_ART = {
  aendern: 'Ändern',
  streichen: 'Streichen',
  ergaenzen: 'Ergänzen',
  hinweis: 'Hinweis',
};

/** Ein Beitrag, der als Schreiben zum Gliedern taugt: vom Menschen, lang, kein Auftrag. */
function istSchreiben(beitrag) {
  return beitrag?.rolle === 'mensch' && beitrag?.aus !== 'gliederung'
    && String(beitrag?.text || '').length >= GLIEDERUNG_MINDESTLAENGE;
}

/** Steht im Gespräch ein Schreiben, das sich gliedern lässt? */
export function gliederungsQuelleDa(verlauf) {
  return (verlauf || []).some(istSchreiben);
}

// Dieselbe Regel wie am Server (quellenTitel in api/_lib/verhandlungsGliederung.js):
// von einer angehängten Datei der Dateiname, sonst die erste Zeile.
const DATEINAME = /^(?:Inhalt der Datei|Text aus dem Bild) „([^"“”\n]{1,160})["“”]/;

/** Wie die Gliederung ihre Quelle nennt - "Anwalt.pdf" statt der ganzen Kopfzeile der Datei. */
export function quellenTitel(text) {
  const erste = String(text || '').split('\n')[0].trim();
  const datei = erste.match(DATEINAME);
  return (datei ? datei[1] : erste).slice(0, 120);
}

/** Das jüngste Schreiben im Gespräch, das sich gliedern lässt - oder null. */
export function neuestesSchreiben(verlauf) {
  const liste = verlauf || [];
  for (let i = liste.length - 1; i >= 0; i -= 1) {
    if (istSchreiben(liste[i])) return liste[i];
  }
  return null;
}

/**
 * Den Hinweis "Erst gliedern" zeigen, solange das neueste Schreiben nicht
 * gegliedert ist - auch wenn danach noch Nachrichten kamen. Bis zum
 * 29.09.2026 stand er nur, solange das Schreiben der LETZTE Beitrag war; eine
 * zweite Nachricht ("Können wir das durcharbeiten?") ließ ihn verschwinden.
 * Erkannt wird das gegliederte Schreiben an seiner Zeit (quelleZeit), bei
 * älteren Gliederungen am Namen. uebersprungen: die Zeit eines Schreibens,
 * bei dem der Bearbeiter „Ohne Gliederung weiter“ gewählt hat.
 */
export function gliedernEmpfohlen(verlauf, gliederung = null, uebersprungen = '') {
  const schreiben = neuestesSchreiben(verlauf);
  if (!schreiben) return false;
  if (uebersprungen && schreiben.zeit === uebersprungen) return false;
  if (!gliederung) return true;
  if (gliederung.quelleZeit) return gliederung.quelleZeit !== schreiben.zeit;
  return gliederung.quelle !== quellenTitel(schreiben.text);
}

export function gliedernNutzlast({ vertragsHtml, vertragsart }) {
  return { op: 'gliedern', vertragsHtml, vertragsart };
}

/** Wahl, Notiz oder "entworfen" einzelner Punkte: [{ id, gewaehlt?, notiz?, entworfen? }]. */
export function gliederungsPunkteNutzlast(punkte) {
  return { op: 'gliederung-punkte', punkte };
}

/**
 * Eine Runde "Ausgewählte entwerfen". Der Auftrag an die KI entsteht am
 * Server aus den gespeicherten Punkten - hier gehen nur die Kennungen mit.
 */
export function gliederungEntwerfenNutzlast({ punktIds, vertragsart, paragraphen, vertragsHtml }) {
  return { op: 'chat', gliederungsPunkte: punktIds, vertragsart, paragraphen, vertragsHtml };
}

/** Die offenen Punkte (gewählt, noch nicht entworfen) in Portionen. */
export function gliederungsPortionen(punkte, groesse = GLIEDERUNG_PORTION) {
  const offen = (punkte || []).filter((p) => p.gewaehlt && !p.entworfen);
  const portionen = [];
  for (let i = 0; i < offen.length; i += groesse) portionen.push(offen.slice(i, i + groesse));
  return portionen;
}

export function gliederungZaehler(gliederung) {
  const punkte = gliederung?.punkte || [];
  return {
    gesamt: punkte.length,
    gewaehlt: punkte.filter((p) => p.gewaehlt).length,
    entworfen: punkte.filter((p) => p.entworfen).length,
    offen: punkte.filter((p) => p.gewaehlt && !p.entworfen).length,
  };
}

/** "Entwirft Punkt 6 bis 10 von 14 …" während "Ausgewählte entwerfen". */
export function gliederungFortschritt({ fertig, gesamt, portion }) {
  const von = fertig + 1;
  const bis = fertig + portion;
  const welche = von === bis ? `Punkt ${von}` : `Punkt ${von} bis ${bis}`;
  return `Entwirft ${welche} von ${gesamt} …`;
}

export function entwerfenKnopfText(offen) {
  return offen === 1 ? 'Ausgewählten Punkt entwerfen' : `${offen} ausgewählte Punkte entwerfen`;
}

/**
 * Wahl, Notiz und "entworfen" sofort anzeigen, während das Speichern noch
 * läuft - wie der Server (aendereGliederungsPunkte): nur mitgeschickte
 * Felder, unbekannte Kennungen werden übergangen. Sonst sperrte jedes
 * Häkchen die Liste, bis Supabase geantwortet hat.
 */
export function gliederungMitAenderungen(gliederung, aenderungen) {
  if (!gliederung) return gliederung;
  const nachId = new Map((aenderungen || []).filter((a) => a?.id).map((a) => [a.id, a]));
  return {
    ...gliederung,
    punkte: (gliederung.punkte || []).map((p) => {
      const a = nachId.get(p.id);
      if (!a) return p;
      return {
        ...p,
        ...(typeof a.gewaehlt === 'boolean' ? { gewaehlt: a.gewaehlt } : {}),
        ...(typeof a.notiz === 'string' ? { notiz: a.notiz } : {}),
        ...(typeof a.entworfen === 'boolean' ? { entworfen: a.entworfen } : {}),
      };
    }),
  };
}

/** Was "Ausgewählte entwerfen" insgesamt angelegt hat: neue Ergänzungen und Ersetzungen. */
export function entwurfsBilanz(vorher, nachher) {
  const neu = (liste, alt) => {
    const bekannt = new Set((alt || []).map((x) => x.id));
    return (liste || []).filter((x) => !bekannt.has(x.id)).length;
  };
  return {
    ergaenzungen: neu(nachher?.passus, vorher?.passus),
    ersetzungen: neu(nachher?.aenderungen, vorher?.aenderungen),
  };
}

export function entwurfsBilanzText({ punkte, ergaenzungen, ersetzungen }) {
  const teile = [];
  if (ergaenzungen) teile.push(`${ergaenzungen} ${ergaenzungen === 1 ? 'neue Ergänzung' : 'neue Ergänzungen'}`);
  if (ersetzungen) teile.push(`${ersetzungen} ${ersetzungen === 1 ? 'neue Ersetzung' : 'neue Ersetzungen'}`);
  const kopf = `${punkte} ${punkte === 1 ? 'Punkt' : 'Punkte'} entworfen`;
  return teile.length
    ? `${kopf}: ${teile.join(' · ')} — bitte prüfen und freigeben.`
    : `${kopf} — die KI hat nichts Neues angelegt. Ihre Antwort steht im Gespräch.`;
}

// ---------- Referenz im Studio ----------

/**
 * Welcher Vertrag im Desktop-Studio die Referenz des Gesprächs ist.
 *
 * Beim Erstellen ist es die Vorlage - auch wenn schon gefüllt ist (seit
 * 24.10.2026). Zugestimmte Ersetzungen kommen beim Füllen und Abschließen in
 * die Vorlage beider Varianten (ersetzungenInVarianten); zitierte die KI aus dem
 * gefüllten Vertrag (Beträge, Namen statt {{…}}), ließ sich zustimmen, griff aber
 * in der Vorlage nicht. Bis dahin war es der gefüllte Vertrag, und Ersetzungen
 * kamen nur über „… in den Vertrag übernehmen“ in die aktive Variante.
 *
 * Wird ein Vertrag BEARBEITET - als HTML unter „Bearbeiten“ hochgeladen oder
 * ein gefüllter Vertrag über „Bearbeiten“ geöffnet -, ist allein dieser
 * Vertrag die Referenz: Die KI liest ihn, die Abschnitte kommen aus ihm, und
 * Ersetzungen landen in ihm. Bis zum 29.09.2026 blieb die Vorlage die
 * Referenz; im Bereich „Bearbeiten“ ist sie leer, die KI kannte also keine
 * Paragraphen, und übernommene Ersetzungen gingen in die unsichtbare Vorlage
 * statt in den hochgeladenen Vertrag.
 *
 * @returns {{ html: string, abschnitteHtml: string, ersetzungenIn: 'vertrag' | 'vorlage' }}
 */
export function gespraechsReferenz({ bearbeitet = false, vertragHtml = '', vorlageHtml = '' } = {}) {
  if (bearbeitet) {
    const html = vertragHtml || '';
    return { html, abschnitteHtml: html, ersetzungenIn: 'vertrag' };
  }
  return { html: vorlageHtml || '', abschnitteHtml: vorlageHtml || '', ersetzungenIn: 'vorlage' };
}

// ---------- Wo steht eine Ersetzung, und was fehlt noch? (seit 30.09.2026) ----------
//
// "In der Vorschau" suchte bei einer Ersetzung nur den NEUEN Wortlaut. Der steht
// erst nach "… in den Vertrag übernehmen" im Vertrag - vorher kam "Im erzeugten
// Vertrag ist diese Stelle nicht zu finden. Schon erzeugt?", und der Nutzer hielt
// die zugestimmte Ersetzung fuer kaputt.

/**
 * Sucht erst den neuen, dann den alten Wortlaut. `finde(text)` springt in der
 * Vorschau dorthin und meldet, ob es die Stelle gab.
 * @returns {'neu' | 'alt' | null}
 */
export function sucheErsetzungImVertrag(aenderung, finde) {
  const neu = String(aenderung?.neu || '').trim();
  const alt = String(aenderung?.fundstelle || '').trim();
  if (neu && finde(neu)) return 'neu';
  if (alt && finde(alt)) return 'alt';
  return null;
}

/**
 * Meldung nach "In der Vorschau" - null, wenn der Sprung für sich spricht.
 * `erstellen`: Beim Erstellen gibt es keinen Knopf zum Übernehmen - zugestimmte
 * Ersetzungen kommen mit dem Füllen hinein (seit 24.10.2026).
 */
export function ersetzungVorschauHinweis(aenderung, gefunden, { erstellen = false } = {}) {
  if (gefunden === 'neu') return null;
  const streichen = !String(aenderung?.neu || '').trim();
  const zugestimmt = aenderung?.stand === 'zugestimmt';
  const naechsterSchritt = erstellen
    ? (zugestimmt
      ? 'Sie kommt beim Füllen und Abschließen hinein – Vorschau neu füllen.'
      : 'Erst zustimmen – dann kommt sie beim Füllen und Abschließen hinein.')
    : (zugestimmt
      ? 'Unten auf „… in den Vertrag übernehmen“ klicken.'
      : 'Erst zustimmen, dann unten „… in den Vertrag übernehmen“.');
  if (gefunden === 'alt') {
    return {
      ton: 'info',
      text: `${streichen ? 'Noch nicht gestrichen' : 'Noch nicht im Vertrag – hier steht noch der alte Wortlaut'}. ${naechsterSchritt}`,
    };
  }
  // Vorsichtig formuliert: Die Suche nimmt nur die ersten 60 Zeichen und laesst sehr
  // kurze Wortlaute aus - "nicht gefunden" heisst also nicht sicher "steht nicht drin".
  return streichen
    ? { ton: 'info', text: 'Diese Stelle ist in der Vorschau nicht (mehr) zu finden – schon gestrichen?' }
    : { ton: 'info', text: 'In der Vorschau nicht zu finden – weder der alte noch der neue Wortlaut ließ sich aufspüren.' };
}

/** Meldung, wenn "In der Vorschau" eine Ergänzung nicht im Vertrag findet. */
export function ergaenzungVorschauHinweis({ stand, einfuegenMoeglich }) {
  if (stand !== 'passt') return 'Noch nicht im Vertrag. Erst freigeben, dann einsetzen.';
  return einfuegenMoeglich
    ? 'Noch nicht im Vertrag. Unten auf „… in den Vertrag einfügen“ klicken.'
    : 'Noch nicht im Vertrag – freigegebene Ergänzungen kommen mit dem Füllen hinein.';
}

/**
 * Beim Bearbeiten kommt ein Zustimmen bzw. Freigeben erst über einen eigenen Knopf
 * in den Vertrag - daran erinnert der Hinweis gleich nach dem Klick. Beim
 * Erstellen (`erstellen`) kommt beides von selbst mit Füllen und Abschließen
 * hinein; ist die Vorschau schon gefüllt, füllt das Studio sie neu.
 * @returns {string | null}
 */
export function hinweisNachFreigabe({ art, stand, bearbeitet, erstellen = false }) {
  if (bearbeitet) {
    if (art === 'ersetzung' && stand === 'zugestimmt') {
      return 'Zugestimmt. In den Vertrag kommt die Ersetzung mit „… in den Vertrag übernehmen“ unten.';
    }
    if (art === 'ergaenzung' && stand === 'passt') {
      return 'Freigegeben. In den Vertrag kommt die Ergänzung mit „… in den Vertrag einfügen“ unten.';
    }
    return null;
  }
  if (!erstellen) return null;
  if (art === 'ersetzung' && stand === 'zugestimmt') {
    return 'Zugestimmt. Die Ersetzung kommt beim Füllen und Abschließen in den Vertrag – in beide Varianten.';
  }
  if (art === 'ergaenzung' && stand === 'passt') {
    return 'Freigegeben. Die Ergänzung kommt beim Füllen und Abschließen in den Vertrag – in beide Varianten.';
  }
  return null;
}

// ---------- Ersetzungen beim Erstellen ----------

/**
 * Wendet die zugestimmten Ersetzungen eines Vorgangs auf den Vertragstext an.
 *
 * Der Server tut das beim Erzeugen NICHT selbst: op "aenderungen-anwenden"
 * gibt nur das geänderte HTML zurück und speichert nichts. Wer einen Vertrag
 * erzeugt, ruft das vorher selbst auf - sonst wirken zugestimmte Ersetzungen
 * nicht: das Handy vor Vorschau und Abschließen, das Desktop-Studio beim
 * Erstellen über ersetzungenInVarianten (seit 24.10.2026), beim Bearbeiten über
 * „… in den Vertrag übernehmen“.
 *
 * @returns {Promise<{ html: string, angewandt: number, fehlgeschlagen: number, gruende: string[] }>}
 */
export async function ersetzungenAnwenden({ apiBase, authHeaders, dealId, contractKey, vertragsHtml }) {
  const antwort = await axios.post(
    gespraechPostAdresse(apiBase),
    gespraechKoerper(dealId, contractKey, { op: 'aenderungen-anwenden', vertragsHtml }),
    { headers: authHeaders },
  );
  const daten = antwort.data || {};
  return {
    html: typeof daten.html === 'string' && daten.html ? daten.html : vertragsHtml,
    angewandt: Number(daten.angewandt) || 0,
    fehlgeschlagen: Number(daten.fehlgeschlagen) || 0,
    gruende: (daten.ergebnisse || []).filter((e) => !e.erfolg).map((e) => e.grund),
  };
}

/**
 * Beim Erstellen am Desktop: zugestimmte Ersetzungen auf die Vorlage JEDER
 * Variante anwenden, vor Füllen und Abschließen (seit 24.10.2026).
 *
 * Vorher setzte „… in den Vertrag übernehmen“ das Ergebnis nur als Vorlage der
 * aktiven Variante ein: Die Druckversion ging ohne die Ersetzung nach Bitrix,
 * die Vorschau zeigte sie nicht, und ein Variantenwechsel oder Neuladen der
 * Vorlage warf sie still wieder hinaus. Jetzt gilt immer die gespeicherte
 * Vorlage plus die zugestimmten Ersetzungen - nacheinander, eine Anfrage je
 * Variante.
 *
 * @param {{ varianten: Array<{ key: string, label: string, html: string }> }} p
 * @returns {Promise<Array<{ key, label, html, angewandt, fehlgeschlagen, gruende }>>}
 */
export async function ersetzungenInVarianten({ apiBase, authHeaders, dealId, contractKey, varianten = [] }) {
  const ergebnisse = [];
  for (const variante of varianten) {
    const ergebnis = await ersetzungenAnwenden({
      apiBase, authHeaders, dealId, contractKey, vertragsHtml: variante.html,
    });
    ergebnisse.push({ ...variante, ...ergebnis });
  }
  return ergebnisse;
}

/**
 * '' wenn jede zugestimmte Ersetzung in jeder Variante gegriffen hat, sonst ein
 * Satz mit Variante und Grund - für die Warnung beim Füllen und den Abbruch
 * beim Abschließen.
 */
export function ersetzungsFehlerText(ergebnisse = []) {
  const fehler = (ergebnisse || []).filter((e) => Number(e?.fehlgeschlagen) > 0);
  if (!fehler.length) return '';
  const teile = fehler.map((e) => {
    const gruende = Array.from(new Set((e.gruende || []).filter(Boolean)));
    return `${e.label || e.key}: ${ersetzungenWort(e.fehlgeschlagen)} nicht anwendbar.${gruende.length ? ` ${gruende.join(' ')}` : ''}`;
  });
  return `${teile.join(' ')} Bitte im Reiter „Ersetzungen“ ablehnen oder neu fassen lassen.`;
}
